import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Lightbulb, Save, RotateCcw } from 'lucide-react';
import { VoiceSettings, CreateVoiceSettingsRequest, voiceSettingsService } from '../../services/voiceSettingsService';
import toast from 'react-hot-toast';

interface VoiceSettingsEditorProps {
  voiceSetting: VoiceSettings | null; // null for create, object for edit
  onSave: (data: CreateVoiceSettingsRequest) => Promise<void>;
  onClose: () => void;
}

const VoiceSettingsEditor: React.FC<VoiceSettingsEditorProps> = ({
  voiceSetting,
  onSave,
  onClose
}) => {
  const [formData, setFormData] = useState<CreateVoiceSettingsRequest>(
    voiceSettingsService.getDefaultFormData()
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);

  useEffect(() => {
    if (voiceSetting) {
      // Editing existing voice setting
      setFormData({
        name: voiceSetting.name,
        description: voiceSetting.description || '',
        tone: voiceSetting.tone,
        perspective: voiceSetting.perspective,
        style: voiceSetting.style,
        avoid_terms: [...voiceSetting.avoid_terms],
        include_elements: [...voiceSetting.include_elements],
        preferred_length: voiceSetting.preferred_length,
        cta_style: voiceSetting.cta_style,
        is_default: voiceSetting.is_default
      });
    } else {
      // Creating new voice setting
      setFormData(voiceSettingsService.getDefaultFormData());
    }
  }, [voiceSetting]);

  const handleInputChange = (field: keyof CreateVoiceSettingsRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleArrayAdd = (field: 'avoid_terms' | 'include_elements', value: string) => {
    if (value.trim()) {
      const currentArray = formData[field] as string[];
      if (!currentArray.includes(value.trim())) {
        handleInputChange(field, [...currentArray, value.trim()]);
      }
    }
  };

  const handleArrayRemove = (field: 'avoid_terms' | 'include_elements', index: number) => {
    const currentArray = formData[field] as string[];
    handleInputChange(field, currentArray.filter((_, i) => i !== index));
  };

  const handlePresetApply = (preset: Omit<CreateVoiceSettingsRequest, 'name'>) => {
    setFormData(prev => ({
      ...prev,
      ...preset,
      name: prev.name // Keep the current name
    }));
    setShowPresets(false);
    toast.success('Preset applied successfully');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = voiceSettingsService.validateVoiceSettings(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (err) {
      console.error('Error saving voice setting:', err);
      toast.error('Failed to save voice setting');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (voiceSetting) {
      // Reset to original values
      setFormData({
        name: voiceSetting.name,
        description: voiceSetting.description || '',
        tone: voiceSetting.tone,
        perspective: voiceSetting.perspective,
        style: voiceSetting.style,
        avoid_terms: [...voiceSetting.avoid_terms],
        include_elements: [...voiceSetting.include_elements],
        preferred_length: voiceSetting.preferred_length,
        cta_style: voiceSetting.cta_style,
        is_default: voiceSetting.is_default
      });
    } else {
      // Reset to defaults
      setFormData(voiceSettingsService.getDefaultFormData());
    }
    setErrors([]);
    toast.success('Form reset to defaults');
  };

  const presets = voiceSettingsService.getPresetTemplates();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {voiceSetting ? 'Edit Voice Setting' : 'Create Voice Setting'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure the tone and style for AI-generated campaign content
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
            <ul className="text-sm text-red-700 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Professional & Warm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Length *
              </label>
              <select
                value={formData.preferred_length}
                onChange={(e) => handleInputChange('preferred_length', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="150-250 words">Short (150-250 words)</option>
                <option value="250-400 words">Medium (250-400 words)</option>
                <option value="300-500 words">Standard (300-500 words)</option>
                <option value="400-600 words">Long (400-600 words)</option>
                <option value="500-800 words">Extended (500-800 words)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Brief description of when to use this voice setting..."
            />
          </div>

          {/* Voice Characteristics */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Voice Characteristics</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone *
                </label>
                <input
                  type="text"
                  value={formData.tone}
                  onChange={(e) => handleInputChange('tone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., warm, supportive, professional"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe the emotional tone (warm, professional, urgent, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Perspective *
                </label>
                <input
                  type="text"
                  value={formData.perspective}
                  onChange={(e) => handleInputChange('perspective', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., understanding parent community"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  The viewpoint or stance the content should take
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Writing Style *
                </label>
                <input
                  type="text"
                  value={formData.style}
                  onChange={(e) => handleInputChange('style', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., conversational but authoritative"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  How the content should be written (formal, casual, etc.)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call-to-Action Style *
                </label>
                <input
                  type="text"
                  value={formData.cta_style}
                  onChange={(e) => handleInputChange('cta_style', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., gentle encouragement"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  How to phrase calls-to-action (gentle, direct, urgent, etc.)
                </p>
              </div>
            </div>
          </div>

          {/* Content Guidelines */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Content Guidelines</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Include Elements */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Always Include
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.include_elements.map((element, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded text-sm"
                      >
                        {element}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('include_elements', index)}
                          className="ml-1 text-green-600 hover:text-green-800"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Add element to always include..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const target = e.target as HTMLInputElement;
                          handleArrayAdd('include_elements', target.value);
                          target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = (e.currentTarget as HTMLElement).previousElementSibling as HTMLInputElement;
                        handleArrayAdd('include_elements', input.value);
                        input.value = '';
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded-r-lg hover:bg-green-700"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Elements that should always be included in the content
                </p>
              </div>

              {/* Avoid Terms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Avoid Terms/Phrases
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.avoid_terms.map((term, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 rounded text-sm"
                      >
                        {term}
                        <button
                          type="button"
                          onClick={() => handleArrayRemove('avoid_terms', index)}
                          className="ml-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex">
                    <input
                      type="text"
                      placeholder="Add term to avoid..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const target = e.target as HTMLInputElement;
                          handleArrayAdd('avoid_terms', target.value);
                          target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = (e.currentTarget as HTMLElement).previousElementSibling as HTMLInputElement;
                        handleArrayAdd('avoid_terms', input.value);
                        input.value = '';
                      }}
                      className="px-3 py-2 bg-red-600 text-white rounded-r-lg hover:bg-red-700"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Terms or phrases that should be avoided in the content
                </p>
              </div>
            </div>
          </div>

          {/* Default Setting */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_default"
              checked={formData.is_default}
              onChange={(e) => handleInputChange('is_default', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_default" className="ml-2 text-sm text-gray-700">
              Set as default voice setting
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setShowPresets(!showPresets)}
                className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Lightbulb className="h-4 w-4 mr-1" />
                Presets
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reset
              </button>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {voiceSetting ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Presets Panel */}
          {showPresets && (
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Choose a Preset</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {presets.map((preset, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition-colors"
                    onClick={() => handlePresetApply(preset)}
                  >
                    <div className="text-sm font-medium text-gray-900 mb-2">
                      {preset.tone}
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      {preset.description}
                    </div>
                    <div className="text-xs text-blue-600">
                      Click to apply
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default VoiceSettingsEditor;